import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authManager } from '../../lib/directus';
import { clearAuthState, syncDirectusAuth } from '../../store/slices/authSlice';
import { RootState } from '../../store/store';
import { persistor } from '../../store/store';
import { tokenCache } from '../../lib/tokenCache';

interface AuthTokenValidatorProps {
  children: React.ReactNode;
  onValidationComplete: () => void;
}

/**
 * Fast and reliable token validator with intelligent refresh logic
 * - Prioritizes speed with cached validations
 * - Attempts token refresh before logout
 * - Handles network vs auth errors properly
 */
export const AuthTokenValidator: React.FC<AuthTokenValidatorProps> = ({ 
  children, 
  onValidationComplete 
}) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const validationStartedRef = useRef(false);
  const [validationStatus, setValidationStatus] = useState<'checking' | 'refreshing' | 'complete'>('checking');

  useEffect(() => {
    const fastValidateAuth = async () => {
      // Prevent duplicate validation
      if (validationStartedRef.current) {
        return;
      }
      validationStartedRef.current = true;

      const validationStart = Date.now();

      try {
        console.log('⚡ Fast token validation starting...');
        setValidationStatus('checking');

        // Quick state checks first (no network calls)
        const authState = authManager.getState();
        
        // Fast path: No tokens anywhere
        if (!authState.token && !isAuthenticated) {
          console.log('✅ No tokens - validation complete (0ms)');
          setValidationStatus('complete');
          onValidationComplete();
          return;
        }

        // Fast path: Sync issue, clear Redux immediately
        if (!authState.token && isAuthenticated) {
          console.log('⚡ Clearing stale Redux state (no network call)');
          dispatch(clearAuthState());
          setValidationStatus('complete');
          onValidationComplete();
          return;
        }

        // Fast path: No token in manager
        if (!authState.token) {
          await quickClearCache();
          setValidationStatus('complete');
          onValidationComplete();
          return;
        }

        // Check token expiry locally first (no network call)
        const tokenInfo = authManager.getTokenInfo();
        console.log('📊 Token info:', {
          hasToken: tokenInfo.hasToken,
          isExpired: tokenInfo.isExpired,
          willExpireSoon: tokenInfo.willExpireSoon,
          timeUntilExpiry: tokenInfo.timeUntilExpiry
        });

        // If token is clearly expired, attempt refresh first
        if (tokenInfo.isExpired || tokenInfo.willExpireSoon) {
          // Token expired/expiring, attempting refresh...
          setValidationStatus('refreshing');
          
          const refreshed = await attemptTokenRefresh();
          if (refreshed) {
            console.log(`✅ Token refreshed successfully (${Date.now() - validationStart}ms)`);
            const newAuthState = authManager.getState();
            dispatch(syncDirectusAuth({
              user: newAuthState.user,
              isAuthenticated: true
            }));
            setValidationStatus('complete');
            onValidationComplete();
            return;
          } else {
            console.log('❌ Token refresh failed, logging out');
            await quickClearCache();
            setValidationStatus('complete');
            onValidationComplete();
            return;
          }
        }

        // Token seems valid locally, do quick server validation
        console.log('🌐 Quick server validation...');
        const isValid = await quickServerValidation();
        
        if (isValid) {
          console.log(`✅ Token valid (${Date.now() - validationStart}ms)`);
          dispatch(syncDirectusAuth({
            user: authState.user,
            isAuthenticated: true
          }));
        } else {
          // Initial validation failed, attempting refresh...
          setValidationStatus('refreshing');
          
          const refreshed = await attemptTokenRefresh();
          if (refreshed) {
            console.log(`✅ Validation failed but refresh succeeded (${Date.now() - validationStart}ms)`);
            const newAuthState = authManager.getState();
            dispatch(syncDirectusAuth({
              user: newAuthState.user,
              isAuthenticated: true
            }));
          } else {
            console.log('❌ Validation and refresh failed, logging out');
            await quickClearCache();
          }
        }
        
      } catch (error) {
        console.error('❌ Fast validation error:', error);
        // Try refresh once more before giving up
        if (validationStatus === 'checking') {
          setValidationStatus('refreshing');
          const refreshed = await attemptTokenRefresh();
          if (!refreshed) {
            await quickClearCache();
          }
        } else {
          await quickClearCache();
        }
      } finally {
        setValidationStatus('complete');
        onValidationComplete();
        console.log(`📊 Total validation time: ${Date.now() - validationStart}ms`);
      }
    };

    // Fast cache clearing without unnecessary operations
    const quickClearCache = async () => {
      try {
        console.log('⚡ Quick cache clear...');
        
        // Clear token validation cache first
        tokenCache.clearCache();
        
        // Clear auth manager (includes localStorage)
        await authManager.logout();
        
        // Clear Redux state
        dispatch(clearAuthState());
        
        // Clear persisted data
        await persistor.purge();
        
        console.log('✅ Cache cleared');
      } catch (error) {
        console.error('❌ Cache clear error:', error);
        // Force clear localStorage as fallback
        localStorage.removeItem('directus_auth');
        localStorage.removeItem('directus_creds');
      }
    };

    // Attempt token refresh with timeout and enhanced error handling
    const attemptTokenRefresh = async (): Promise<boolean> => {
      try {
        // Attempting intelligent token refresh...
        
        // Set timeout for refresh operation
        const refreshPromise = authManager.forceTokenRefresh();
        const timeoutPromise = new Promise<boolean>((_, reject) => 
          setTimeout(() => reject(new Error('Refresh timeout')), 8000) // 8s timeout
        );
        
        const success = await Promise.race([refreshPromise, timeoutPromise]);
        
        if (success) {
          console.log('✅ Token refresh successful');
          return true;
        } else {
          console.log('❌ Token refresh returned false');
          return false;
        }
      } catch (error: any) {
        const errorType = classifyError(error);
        console.log(`❌ Token refresh failed (${errorType}):`, error?.message || error);
        
        // For network errors, don't immediately logout - user might come back online
        if (errorType === 'network' || errorType === 'timeout') {
          console.log('🌐 Network issue during refresh, deferring logout decision');
          return false; // Still return false but without clearing cache immediately
        }
        
        // For auth errors, token is definitely invalid
        if (errorType === 'auth') {
          console.log('🔐 Authentication error during refresh, token is invalid');
          return false;
        }
        
        // For server errors, give benefit of doubt
        console.log('🔧 Server error during refresh, will retry later');
        return false;
      }
    };

    // Enhanced error classification
    const classifyError = (error: any): 'network' | 'auth' | 'server' | 'timeout' => {
      if (!error) return 'server';
      
      // Network errors
      if (error.message?.includes('timeout') || error.message?.includes('Validation timeout')) {
        return 'timeout';
      }
      if (error.code === 'NETWORK_ERROR' || !navigator.onLine || error.message?.includes('fetch')) {
        return 'network';
      }
      
      // Authentication errors
      if (error.status === 401 || error.status === 403) {
        return 'auth';
      }
      
      // Server errors (5xx)
      if (error.status >= 500) {
        return 'server';
      }
      
      return 'server'; // Default
    };

    // Quick server validation with caching and enhanced error handling
    const quickServerValidation = async (): Promise<boolean> => {
      const authState = authManager.getState();
      if (!authState.token) return false;

      // Check cache first for ultra-fast validation
      const cachedResult = tokenCache.getCachedResult(authState.token);
      if (cachedResult !== null) {
        console.log(`⚡ Ultra-fast cached validation: ${cachedResult}`);
        return cachedResult;
      }

      // Skip validation if we just validated recently
      if (tokenCache.shouldSkipValidation(authState.token)) {
        console.log('⚡ Skipping validation - too recent');
        return true;
      }

      try {
        // Use the existing validateCurrentToken but with timeout
        const validationPromise = authManager.validateCurrentToken();
        const timeoutPromise = new Promise<boolean>((_, reject) => 
          setTimeout(() => reject(new Error('Validation timeout')), 5000) // 5s timeout
        );
        
        const result = await Promise.race([validationPromise, timeoutPromise]);
        
        // Cache the result for future use
        tokenCache.cacheResult(authState.token, result);
        
        return result;
      } catch (error: any) {
        const errorType = classifyError(error);
        console.log(`❌ Quick validation failed (${errorType}):`, error?.message || error);
        
        // For network/timeout errors, don't immediately invalidate
        if (errorType === 'network' || errorType === 'timeout') {
          console.log('🌐 Network issue detected, treating as offline validation');
          // Don't cache network errors, but assume token might still be valid
          return true; 
        }
        
        // Cache auth failures
        if (errorType === 'auth') {
          tokenCache.cacheResult(authState.token, false);
        }
        
        return false;
      }
    };

    // Start validation immediately (no delay)
    fastValidateAuth();

    return () => {
      validationStartedRef.current = false;
    };
  }, [dispatch, isAuthenticated, onValidationComplete]);

  return <>{children}</>;
};